'use client';

import { useEffect, useRef, useCallback } from 'react';

interface ReCaptchaEnterpriseProps {
  sitekey: string;
  action?: string;
  onChange?: (token: string | null) => void;
  onExpired?: () => void;
  onError?: () => void;
}

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (cb: () => void) => void;
        render: (container: HTMLElement, params: Record<string, unknown>) => number;
        getResponse: (widgetId: number) => string;
        reset: (widgetId?: number) => void;
      };
    };
  }
}

let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks: (() => void)[] = [];

function loadEnterpriseScript(): Promise<void> {
  if (scriptLoaded) return Promise.resolve();

  return new Promise((resolve) => {
    if (scriptLoading) {
      loadCallbacks.push(resolve);
      return;
    }

    scriptLoading = true;
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/enterprise.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => {
      scriptLoading = false;
      resolve(); // resolve anyway to not block UI
    };
    document.head.appendChild(script);
  });
}

export default function ReCaptchaEnterprise({
  sitekey,
  action = 'SUBMIT',
  onChange,
  onExpired,
  onError,
}: ReCaptchaEnterpriseProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  const handleCallback = useCallback(
    (token: string) => {
      onChange?.(token);
    },
    [onChange]
  );

  const handleExpired = useCallback(() => {
    onChange?.(null);
    onExpired?.();
  }, [onChange, onExpired]);

  const handleError = useCallback(() => {
    onChange?.(null);
    onError?.();
  }, [onChange, onError]);

  useEffect(() => {
    let mounted = true;

    loadEnterpriseScript().then(() => {
      if (!mounted || !containerRef.current) return;
      if (widgetIdRef.current !== null) return; // already rendered

      window.grecaptcha.enterprise.ready(() => {
        if (!mounted || !containerRef.current) return;
        if (widgetIdRef.current !== null) return;

        try {
          widgetIdRef.current = window.grecaptcha.enterprise.render(
            containerRef.current,
            {
              sitekey,
              action,
              callback: handleCallback,
              'expired-callback': handleExpired,
              'error-callback': handleError,
            }
          );
        } catch (e) {
          // Widget may already be rendered in dev mode (strict mode double-mount)
        }
      });
    });

    return () => {
      mounted = false;
    };
  }, [sitekey, action, handleCallback, handleExpired, handleError]);

  return <div ref={containerRef} />;
}

/**
 * Reset the Enterprise reCAPTCHA widget globally
 */
export function resetEnterpriseCaptcha() {
  if (typeof window !== 'undefined' && window.grecaptcha?.enterprise) {
    try {
      window.grecaptcha.enterprise.reset();
    } catch (e) {
      // ignore
    }
  }
}
